import { useRef, useMemo, useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';

import { IconSearch } from '@tabler/icons-react';

import DateRangePicker from '@/components/dateRangePicker';
import EmptyState from '@/components/EmptyState';
import ExportDropdown from '@/components/ExportDropdown';
import Input from '@/components/Input/Input';
import ProgressBar from '@/components/ProgressBar';
import Select from '@/components/Select';
import TeacherReportSkeleton from '@/components/SkeletonLoader/TeacherReportSkeleton';
import CustomTable from '@/components/Table';
import TagCapsule from '@/components/TagCapsule';
import { EExportType } from '@/enum';
import { useDebounce } from '@/hooks/useDebounce';
import { useFormatText } from '@/hooks/useFormatText';
import usePageTitle from '@/hooks/usePageTitle';
import { getTeacherClassDropdown } from '@/service/classService';
import { getTeacherLessonDropdown } from '@/service/lessonService';
import {
  getTeacherAnalytics,
  exportTeacherAnalytics,
} from '@/service/reportService';
import { useSkeletonStore } from '@/store/useSkeletonStore';
import { IClassDropdownItem } from '@/types';
import { formatQuery, downloadFile, formatLastActive } from '@/utils';

interface ITeacherAnalyticsItem {
  id: string;
  name: string;
  active_students: number;
  lesson_created: number;
  last_update: string;
  avg_completion?: number;
  accuracy?: number;
}

const TeacherReports = () => {
  const { formatText } = useFormatText();
  const { setTitle } = usePageTitle();
  const { isTableLoading, setTableLoading, isScreenLoading, setScreenLoading } =
    useSkeletonStore();

  const [analyticsData, setAnalyticsData] = useState<ITeacherAnalyticsItem[]>(
    []
  );
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [sortField, setSortField] = useState<string | undefined>();
  const [sortDirection, setSortDirection] = useState<'' | 'ASC' | 'DESC'>('');

  const [classOptions, setClassOptions] = useState<
    { value: string; label: string }[]
  >([]);
  const [lessonOptions, setLessonOptions] = useState<
    { value: string; label: string }[]
  >([]);
  const [selectedClasses, setSelectedClasses] = useState<string[]>([]);
  const [selectedLessons, setSelectedLessons] = useState<string[]>([]);

  const [startDate, setStartDate] = useState<string | null>(null);
  const [endDate, setEndDate] = useState<string | null>(null);

  const [isExportLoading, setIsExportLoading] = useState(false);
  const isInitialLoad = useRef(true);
  const pageTitle = formatText('teacher-reports.title');

  useEffect(() => {
    setTitle(pageTitle);
    return () => setTitle('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageTitle]);

  const { register, watch } = useForm({ defaultValues: { search: '' } });
  const search = watch('search');
  const debouncedSearch = useDebounce(search, 500);

  // Reset page on any filter change
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch, selectedClasses, selectedLessons, startDate, endDate]);

  const setLoadingState = (loading: boolean) => {
    if (isInitialLoad.current) setScreenLoading(loading);
    else setTableLoading(loading);
  };

  const fetchClasses = async () => {
    try {
      const response = await getTeacherClassDropdown(true);
      if (response?.data) {
        setClassOptions(
          response.data.map((item: IClassDropdownItem) => ({
            value: item.id,
            label: item.name,
          }))
        );
      }
    } catch (error) {
      console.error('Failed to fetch class dropdown:', error);
    }
  };

  const fetchLessons = async () => {
    try {
      const response = await getTeacherLessonDropdown('');
      if (response?.data) {
        setLessonOptions(
          response.data.map((item: IClassDropdownItem) => ({
            value: item.id,
            label: item.name,
          }))
        );
      }
    } catch (error) {
      console.error('Failed to fetch lesson dropdown:', error);
    }
  };

  const fetchAnalytics = async () => {
    setLoadingState(true);
    try {
      const params = {
        limit: pageSize,
        offset: (currentPage - 1) * pageSize,
        ...(debouncedSearch && { search: debouncedSearch }),
        ...(sortField &&
          sortDirection && {
            [`order[${sortField}]`]: sortDirection,
          }),
        ...(selectedClasses.length > 0 && { class_id: selectedClasses }),
        ...(selectedLessons.length > 0 && { lesson_id: selectedLessons }),
        ...(startDate && {
          start_date: Math.floor(new Date(startDate).getTime() / 1000),
        }),
        ...(endDate && {
          end_date: Math.floor(new Date(endDate).getTime() / 1000),
        }),
      };

      const query = formatQuery(params);
      const response = await getTeacherAnalytics(query);
      if (response?.status) {
        setAnalyticsData(response.data ?? []);
        setTotal(response.total ?? 0);
      } else {
        setAnalyticsData([]);
        setTotal(0);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoadingState(false);
      isInitialLoad.current = false;
    }
  };

  useEffect(() => {
    fetchClasses();
    fetchLessons();
  }, []);

  useEffect(() => {
    fetchAnalytics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentPage,
    debouncedSearch,
    sortField,
    sortDirection,
    selectedClasses,
    selectedLessons,
    startDate,
    endDate,
    pageSize,
  ]);

  const handleSort = (key: string) => {
    if (sortField === key) {
      if (sortDirection === 'ASC') {
        setSortDirection('DESC');
      } else if (sortDirection === 'DESC') {
        setSortField('');
        setSortDirection('');
      } else {
        setSortDirection('ASC');
      }
    } else {
      setSortField(key);
      setSortDirection('ASC');
    }
  };

  const handleExport = async (type: 'csv' | 'pdf') => {
    setIsExportLoading(true);
    try {
      const query: Record<string, unknown> = {};

      if (debouncedSearch) query.search = debouncedSearch;
      if (selectedClasses?.length > 0) query.class_id = selectedClasses;
      if (selectedLessons?.length > 0) query.lesson_id = selectedLessons;
      if (startDate) {
        query.start_date = Math.floor(new Date(startDate).getTime() / 1000);
      }
      if (endDate) {
        query.end_date = Math.floor(new Date(endDate).getTime() / 1000);
      }
      if (type === 'csv') {
        query.type = EExportType.CSV;
      } else if (type === 'pdf') {
        query.type = EExportType.PDF;
      }

      const response = await exportTeacherAnalytics(query);
      if (response) {
        const label = pageTitle?.replace(/\s+/g, '_')?.toLowerCase();
        downloadFile(response, `${label}_${new Date().getTime()}.${type}`);
      }
    } catch (error) {
      console.error('Error exporting content:', error);
    } finally {
      setIsExportLoading(false);
    }
  };

  const columns = useMemo(
    () => [
      {
        key: 'name',
        title: 'teacher-reports.columns.class-assigned',
        sorter: true,
        width: '300px',
        handleSort: () => handleSort('name'),
        render: (record: ITeacherAnalyticsItem) => (
          <span className="text-sm font-medium text-neutral-primary">
            {record?.name}
          </span>
        ),
      },
      {
        key: 'active_students',
        title: 'teacher-reports.columns.active-students',
        sorter: true,
        width: '150px',
        handleSort: () => handleSort('active_students'),
        render: (record: ITeacherAnalyticsItem) => (
          <TagCapsule
            value={record?.active_students?.toString() ?? '0'}
            className="text-success-secondary bg-success-light-50"
          />
        ),
      },
      {
        key: 'lesson_created',
        title: 'teacher-reports.columns.lesson-created',
        sorter: true,
        handleSort: () => handleSort('lesson_created'),
        width: '150px',
        render: (record: ITeacherAnalyticsItem) => (
          <TagCapsule
            value={record?.lesson_created?.toString() ?? '0'}
            className="text-pink-secondary bg-pink-light-50"
          />
        ),
      },
      {
        key: 'avg_completion',
        title: 'teacher-reports.columns.avg-completion',
        sorter: true,
        handleSort: () => handleSort('avg_completion'),
        width: '200px',
        render: (record: ITeacherAnalyticsItem) => (
          <ProgressBar value={record?.avg_completion ?? 0} className="w-50" />
        ),
      },
      {
        key: 'accuracy',
        title: 'teacher-reports.columns.accuracy',
        sorter: true,
        handleSort: () => handleSort('accuracy'),
        width: '150px',
        render: (record: ITeacherAnalyticsItem) => (
          <span className="text-sm text-neutral-secondary">
            {record?.accuracy ?? 0}%
          </span>
        ),
      },
      {
        key: 'last_update',
        title: 'teacher-reports.columns.last-update',
        sorter: true,
        handleSort: () => handleSort('last_update'),
        width: '200px',
        render: (record: ITeacherAnalyticsItem) => (
          <span className="text-sm text-neutral-secondary">
            {formatLastActive(
              record?.last_update ? Number(record.last_update) : 0,
              formatText
            )}
          </span>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [sortField, sortDirection, formatText]
  );

  const isFilterApplied =
    selectedClasses?.length > 0 ||
    selectedLessons?.length > 0 ||
    !!startDate ||
    !!endDate;

  const showListUI =
    isScreenLoading ||
    analyticsData?.length > 0 ||
    search?.length > 0 ||
    !!debouncedSearch ||
    isFilterApplied;

  const isEmptySearch =
    !isScreenLoading &&
    !isTableLoading &&
    analyticsData?.length === 0 &&
    (!!debouncedSearch || isFilterApplied);

  if (!showListUI) {
    return (
      <EmptyState
        title={formatText('teacher-reports.empty-title')}
        description={formatText('teacher-reports.empty-description')}
        imageSrc="/assets/svg/empty-report-analytics.svg"
        className="h-full!"
      />
    );
  }

  if (isScreenLoading) {
    return <TeacherReportSkeleton columns={columns} />;
  }

  return (
    <div className="flex flex-col gap-6" data-testid="teacher-reports-page">
      {/* Filters */}
      <div
        className="flex items-center justify-between gap-4 flex-wrap ps-1"
        data-testid="reports-filter-section"
      >
        <div className="flex flex-1 items-center gap-3 flex-wrap">
          <Input
            name="search"
            register={register}
            placeholder={formatText('teacher-reports.search-placeholder')}
            leftIcon={{
              icon: <IconSearch size={20} className="text-neutral-500" />,
            }}
            className="max-w-70"
            inputClassName="py-[13px]"
          />

          <Select
            options={classOptions}
            selected={selectedClasses}
            onChange={(val) => setSelectedClasses(val as string[])}
            placeholder={formatText('teacher-reports.select-class')}
            className="w-37.5! py-3.25"
            isClearable
            isMulti
          />

          <Select
            options={lessonOptions}
            selected={selectedLessons}
            onChange={(val) => setSelectedLessons(val as string[])}
            placeholder={formatText('teacher-reports.select-lesson')}
            className="w-37.5! py-3.25"
            isClearable
            isMulti
          />
        </div>

        <div className="flex items-center gap-3">
          <DateRangePicker
            setFirstDate={setStartDate}
            setSecondDate={setEndDate}
            popupAlign="right"
          />
          <ExportDropdown
            onCsvExport={() => handleExport('csv')}
            onPdfExport={() => handleExport('pdf')}
            disabled={analyticsData?.length === 0 || isExportLoading}
          />
        </div>
      </div>

      {/* Table */}
      <CustomTable
        isLoading={isTableLoading}
        columns={columns}
        dataSource={analyticsData}
        total={total}
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        pageSize={pageSize}
        fieldName={sortField}
        sortDirection={sortDirection}
        isEmptySearch={isEmptySearch}
        onPageSizeChange={(size) => {
          setPageSize(size);
          setCurrentPage(1);
        }}
      />
    </div>
  );
};

export default TeacherReports;
